import { omit } from "ramda";
import React, { useState, useEffect } from "react";
import { Card } from "react-bootstrap";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

import FormikContainer from "../../../components/form/FormikContainer.jsx";
import DeleteConfirmationModal from "../../../components/modal/DeleteConfirmationModal.jsx";
import genericTranslations from "../../../utils/genericTranslations.js";
import { selectSoftwarePackages } from "../../softwarePackages/selectors.js";
import { fetchSoftwarePackages } from "../../softwarePackages/thunks.js";
import { selectSoftwareNotificationById } from "../selectors.js";
import {
  fetchSoftwareNotificationById,
  removeSoftwareNotification,
  saveSoftwareNotification,
} from "../thunks.js";

import PushNotificationConfirmationModal from "./PushNotificationConfirmationModal.jsx";
import SoftwareNotificationForm from "./SoftwareNotificationForm";

const messages = {
  notification: {
    id: "modules.SoftwarePackage.components.SoftwareNotificationEditor.notification",
    description: "Notification detail title.",
    defaultMessage: "Notification",
  },
  notFoundMessage: {
    id: "modules.SoftwarePackage.components.SoftwareNotificationEditor.notFoundMessage",
    description: "Error message fetching the software notification",
    defaultMessage: "Could not find the software notification!",
  },
  deleteNotification: {
    id: "modules.SoftwarePackage.components.SoftwareNotificationEditor.deleteNotification",
    description: "Delete notification",
    defaultMessage: "Delete Notification",
  },
  deleteNotificationConfirmation: {
    id: "modules.SoftwarePackage.components.SoftwareNotificationEditor.deleteNotificationConfirmation",
    description: "Delete notification confirmation",
    defaultMessage: "Are you sure you want to delete this notification?",
  },
};

const SoftwareNotificationEditor = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { identifier } = useParams();

  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [softwareNotificationCopy, setSoftwareNotificationCopy] = useState({});

  const softwareNotification = useSelector((state) =>
    selectSoftwareNotificationById(state, { identifier }),
  );
  const softwarePackages = useSelector(selectSoftwarePackages);

  useEffect(() => {
    const fetchNotificationData = async () => {
      try {
        await dispatch(fetchSoftwareNotificationById(identifier));
      } catch (err) {
        setError(err);
      }
    };

    const fetchSoftwarePackagesData = async () => {
      try {
        await dispatch(fetchSoftwarePackages());
      } catch (err) {
        console.error("Error fetching software packages:", err);
        setError(err);
      }
    };

    if (identifier && !softwareNotification) {
      fetchNotificationData();
    }

    // Always fetch the entire list of packages to populate the 'tags' dropdown list
    fetchSoftwarePackagesData();
  }, [softwareNotification, identifier, dispatch]);

  const handleSubmit = async (values, { setSubmitting }) => {
    const body = omit(["updatedBy", "softwarePackages"], values);

    if (body.sendNotification) {
      setSoftwareNotificationCopy(body);
      setShowConfirmationModal(true);
      return;
    }
    setSubmitting(true);
    try {
      await dispatch(
        saveSoftwareNotification(
          body,
          softwareNotification ? softwareNotification.id : undefined,
        ),
      );
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    } finally {
      setSubmitting(false);
    }

    navigate("/message", { replace: true });
  };

  const validate = (values) => {
    const errors = {};
    const required = ["title", "tag"];
    required.forEach((field) => {
      if (!values[field]) errors[field] = "Verplicht";
    });
    return errors;
  };

  const handleRemoveNotification = async () => {
    try {
      await dispatch(removeSoftwareNotification(softwareNotification.id));
      navigate("/message", { replace: true });
    } catch (err) {
      setError(`Er ging iets fout bij het opslaan: ${err}`);
    }
  };

  const onSendNotification = async () => {
    const body = {
      title: softwareNotificationCopy.title,
      tag: softwareNotificationCopy.tag,
      pushNotification: false,
      sendNotification: true,
    };
    try {
      await dispatch(
        saveSoftwareNotification(body, softwareNotificationCopy.id),
      );
      navigate("/message", { replace: true });
    } catch (err) {
      setError(err);
    }
  };

  const onSendPushNotification = async () => {
    const body = {
      title: softwareNotificationCopy.title,
      tag: softwareNotificationCopy.tag,
      pushNotification: true,
      sendNotification: true,
    };
    try {
      await dispatch(
        saveSoftwareNotification(body, softwareNotificationCopy.id),
      );
      navigate("/message", { replace: true });
    } catch (err) {
      setError(err);
    }
  };

  if (error) {
    return (
      <strong>{intl.formatMessage(genericTranslations.errorMessage)} </strong>
    );
  }

  if (!softwareNotification && identifier) {
    return <strong>{intl.formatMessage(messages.notFoundMessage)}</strong>;
  }

  const initialValues = {
    ...softwareNotification,
    sendNotification: false,
    softwarePackages,
  };

  return (
    <Card>
      <Card.Header className="card-header-borderless" as="h3">
        {intl.formatMessage(messages.notification)}{" "}
        {softwareNotification
          ? intl.formatMessage(genericTranslations.edit).toLowerCase()
          : intl.formatMessage(genericTranslations.add).toLowerCase()}
      </Card.Header>
      <Card.Body>
        <FormikContainer
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validate={validate}
          formComponent={SoftwareNotificationForm}
          extraProps={{
            onClickDelete: () => setShowDeleteModal(true),
          }}
        />
        <DeleteConfirmationModal
          show={showDeleteModal}
          title={intl.formatMessage(messages.deleteNotification)}
          bodyText={intl.formatMessage(messages.deleteNotificationConfirmation)}
          confirmButtonText={intl.formatMessage(genericTranslations.delete)}
          cancelButtonText={intl.formatMessage(genericTranslations.close)}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleRemoveNotification}
        />
        <PushNotificationConfirmationModal
          show={showConfirmationModal}
          onClose={() => {
            setShowConfirmationModal(false);
            navigate("/message", { replace: true });
          }}
          onSendNotification={onSendNotification}
          onSendPushNotification={onSendPushNotification}
        />
      </Card.Body>
    </Card>
  );
};

export default SoftwareNotificationEditor;
